import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../utils/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  // Инициализируем state из localStorage при первой загрузке
  const initialToken = localStorage.getItem('token');
  const initialRefreshToken = localStorage.getItem('refreshToken');
  
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(initialToken);
  const [, setRefreshToken] = useState(initialRefreshToken);

  // Проверка токена при загрузке приложения и при изменении токена
  useEffect(() => {
    const checkAuth = async () => {
      const storedToken = localStorage.getItem('token');
      const tokenToCheck = token || storedToken;

      // Если токен в state не совпадает с localStorage, синхронизируем
      if (storedToken && storedToken !== token) {
        setToken(storedToken);
        return; // Выходим, useEffect сработает снова с новым токеном
      }
      
      if (tokenToCheck) {
        try {
          const response = await api.get('/auth/me');

          if (response.data && response.data.user) {
            setUser(response.data.user);
            // Убеждаемся, что токен сохранен в state
            if (!token && storedToken) {
              setToken(storedToken);
            }
          } else {
            throw new Error('Invalid response format');
          }
        } catch (error) {
          console.error('❌ [AuthContext] Auth check failed:', {
            status: error.response?.status,
            data: error.response?.data,
            message: error.message
          });
          // Токен недействителен - удаляем его
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          setToken(null);
          setRefreshToken(null);
          setUser(null);
        }
      } else {
        setUser(null);
      }
      setLoading(false);
    };

    checkAuth();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- run on token change only; user used only for logging
  }, [token]);

  // Функция входа
  const login = async (email, password) => {
    try {
      // Личный кабинет (/login) — НЕ отправляем X-Admin-Panel, чтобы не требовать 2FA при входе.
      // Админ-панель (/admin) отправляет свой заголовок отдельно.
      const response = await api.post('/auth/login', { email, password });
      
      // 2FA не требуется при входе через личный кабинет (только через /admin)
      if (response.data.requiresTwoFactor) {
        return {
          success: true,
          requiresTwoFactor: true,
          tempToken: response.data.tempToken,
          user: response.data.user,
          message: response.data.message || 'Введите код из приложения аутентификации'
        };
      }

      const { token: newToken, refreshToken: newRefreshToken, user: userData } = response.data;

      if (!newToken) {
        console.error('❌ [AuthContext] No token in login response!');
        throw new Error('Токен не получен от сервера');
      }
      
      if (!userData) {
        console.error('❌ [AuthContext] No user data in login response!');
        throw new Error('Данные пользователя не получены от сервера');
      }
      
      // БЕЗОПАСНОСТЬ: Сохраняем access и refresh токены
      localStorage.setItem('token', newToken);
      if (newRefreshToken) {
        localStorage.setItem('refreshToken', newRefreshToken);
      }

      setToken(newToken);
      setRefreshToken(newRefreshToken);
      setUser(userData);

      // Проверяем авторизацию через небольшую задержку, чтобы убедиться, что токен работает
      setTimeout(async () => {
        try {
          const verifyResponse = await api.get('/auth/me');
          if (verifyResponse.data && verifyResponse.data.user) {
            setUser(verifyResponse.data.user);
          }
        } catch (error) {
          console.error('⚠️ [AuthContext] Token verification failed after login:', error);
        }
      }, 100);
      
      return { success: true, message: 'Вход выполнен успешно', user: userData };
    } catch (error) {
      console.error('Login error:', error);
      
      // Обработка таймаута
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        return { 
          success: false, 
          message: 'Превышено время ожидания ответа от сервера. Проверьте подключение к интернету и убедитесь, что backend сервер запущен.' 
        };
      }
      
      // Обработка сетевых ошибок
      if (!error.response && error.request) {
        return { 
          success: false, 
          message: 'Не удалось подключиться к серверу. Убедитесь, что backend сервер запущен и доступен.' 
        };
      }
      
      const errorData = error.response?.data || {};
      const message = errorData.message || 'Ошибка при входе';
      const deactivationReason = errorData.deactivationReason;
      const requiresVerification = errorData.requiresVerification;
      const email = errorData.email;
      
      // Если требуется подтверждение email
      if (requiresVerification && error.response?.status === 403) {
        return { 
          success: false, 
          message,
          requiresVerification: true,
          email
        };
      }
      
      // Если есть причина деактивации, формируем специальное сообщение
      if (deactivationReason && error.response?.status === 403) {
        return { 
          success: false, 
          message: `Ваш профиль был заблокирован администрацией по причине: "${deactivationReason}"`,
          deactivationReason 
        };
      }
      
      // Обработка ошибок сервера (503, 500 и т.д.)
      if (error.response?.status >= 500) {
        return { 
          success: false, 
          message: 'Сервер временно недоступен. Попробуйте позже.' 
        };
      }
      
      return { success: false, message };
    }
  };

  // Подтверждение 2FA кода (для админа с включённой двухфакторной аутентификацией)
  const verify2FA = async (tempToken, code) => {
    try {
      const response = await api.post('/auth/2fa/verify', { tempToken, code }, {
        headers: { 'X-Admin-Panel': 'true' }
      });
      const { token: newToken, refreshToken: newRefreshToken, user: userData } = response.data;
      if (!newToken || !userData) {
        throw new Error('Токен не получен от сервера');
      }
      localStorage.setItem('token', newToken);
      if (newRefreshToken) {
        localStorage.setItem('refreshToken', newRefreshToken);
      }
      setToken(newToken);
      setRefreshToken(newRefreshToken);
      setUser(userData);
      return { success: true, message: 'Вход выполнен успешно', user: userData };
    } catch (error) {
      const message = error.response?.data?.message || 'Неверный код. Проверьте код в приложении аутентификации.';
      return { success: false, message };
    }
  };

  // Функция регистрации
  const register = async (userData) => {
    try {
      const response = await api.post('/auth/register', userData);
      
      // Проверяем, требуется ли подтверждение email
      if (response.data.requiresVerification) {
        return { 
          success: true, 
          message: response.data.message || 'Регистрация успешна. Проверьте email для подтверждения.',
          requiresVerification: true,
          userId: response.data.userId,
          email: response.data.email,
          verificationCode: response.data.verificationCode // Код из ответа (только в режиме разработки)
        };
      }
      
      // Старый формат ответа (с токеном) - для обратной совместимости
      const { token: newToken, refreshToken: newRefreshToken, user: newUser } = response.data;
      
      if (newToken && newUser) {
        setToken(newToken);
        setUser(newUser);
        localStorage.setItem('token', newToken);
        if (newRefreshToken) {
          setRefreshToken(newRefreshToken);
          localStorage.setItem('refreshToken', newRefreshToken);
        }
      }
      
      return { success: true, message: 'Регистрация прошла успешно' };
    } catch (error) {
      // Обработка ошибок валидации
      if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        const errorMessages = error.response.data.errors.map(err => 
          err.message || `${err.field}: ${err.msg || 'некорректное значение'}`
        ).join(', ');
        return { success: false, message: `Ошибка валидации: ${errorMessages}` };
      }
      const message = error.response?.data?.message || 'Ошибка при регистрации';
      return { success: false, message };
    }
  };

  // Функция подтверждения email
  const verifyEmail = async (userIdOrEmail, code) => {
    try {
      const requestData = typeof userIdOrEmail === 'number' 
        ? { userId: userIdOrEmail, code }
        : { email: userIdOrEmail, code };
      
      const response = await api.post('/auth/verify-email', requestData);
      const { token: newToken, refreshToken: newRefreshToken, user: userData } = response.data;
      
      setToken(newToken);
      setUser(userData);
      localStorage.setItem('token', newToken);
      if (newRefreshToken) {
        setRefreshToken(newRefreshToken);
        localStorage.setItem('refreshToken', newRefreshToken);
      }
      
      return { success: true, message: response.data.message || 'Email успешно подтвержден' };
    } catch (error) {
      const message = error.response?.data?.message || 'Ошибка при подтверждении email';
      return { success: false, message };
    }
  };

  // Функция повторной отправки кода подтверждения
  const resendVerificationCode = async (email) => {try {
      const response = await api.post('/auth/resend-verification-code', { email });return { 
        success: true, 
        message: response.data.message || 'Код подтверждения отправлен на ваш email' 
      };
    } catch (error) {const message = error.response?.data?.message || 'Ошибка при отправке кода';
      return { success: false, message };
    }
  };

  // Функция выхода
  const logout = async () => {
    try {
      const currentRefreshToken = localStorage.getItem('refreshToken');
      
      // Отправляем запрос на logout для отзыва токенов на сервере
      if (token || currentRefreshToken) {
        try {
          await api.post('/auth/logout', { refreshToken: currentRefreshToken });
        } catch (error) {
          console.error('Logout API error:', error);
          // Продолжаем выход даже при ошибке API
        }
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // БЕЗОПАСНОСТЬ: Очищаем все токены из localStorage
      setToken(null);
      setRefreshToken(null);
      setUser(null);
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
    }
  };

  // Функция обновления профиля
  const updateProfile = async (profileData) => {
    try {
      const response = await api.put('/auth/profile', profileData);
      setUser(response.data.user);
      return { success: true, message: 'Профиль обновлен успешно' };
    } catch (error) {
      const message = error.response?.data?.message || 'Ошибка при обновлении профиля';
      return { success: false, message };
    }
  };

  // Запрос кода сброса пароля
  const forgotPassword = async (email) => {
    try {
      const response = await api.post('/auth/forgot-password', { email });
      return {
        success: true,
        message: response.data.message || 'Код сброса пароля отправлен на ваш email',
        resetCode: response.data.resetCode // Для режима разработки
      };
    } catch (error) {
      const message = error.response?.data?.message || 'Ошибка при отправке кода';
      return { success: false, message };
    }
  };

  // Подтверждение кода сброса пароля
  const verifyPasswordResetCode = async (email, code) => {
    try {
      const response = await api.post('/auth/verify-password-reset-code', { email, code });
      return {
        success: true,
        message: response.data.message || 'Код подтвержден'
      };
    } catch (error) {
      const message = error.response?.data?.message || 'Ошибка при проверке кода';
      return { success: false, message };
    }
  };

  // Сброс пароля после подтверждения кода
  const resetPassword = async (email, code, newPassword) => {
    try {
      const response = await api.post('/auth/reset-password', { email, code, newPassword });
      return {
        success: true,
        message: response.data.message || 'Пароль успешно изменен'
      };
    } catch (error) {
      const message = error.response?.data?.message || 'Ошибка при сбросе пароля';
      return { success: false, message };
    }
  };

  // Повторная отправка кода сброса пароля
  const resendPasswordResetCode = async (email) => {
    try {
      const response = await api.post('/auth/forgot-password', { email });
      return {
        success: true,
        message: response.data.message || 'Код сброса пароля отправлен на ваш email',
        resetCode: response.data.resetCode // Для режима разработки
      };
    } catch (error) {
      const message = error.response?.data?.message || 'Ошибка при отправке кода';
      return { success: false, message };
    }
  };

  // Смена пароля для авторизованных пользователей (только свой пароль; текущий пароль обязателен)
  const changePassword = async (currentPassword, newPassword) => {
    try {
      const response = await api.post('/auth/change-password', {
        currentPassword,
        newPassword
      });
      return {
        success: true,
        message: response.data.message || 'Пароль успешно изменен'
      };
    } catch (error) {
      const message = error.response?.data?.message || 'Ошибка при изменении пароля';
      return { success: false, message };
    }
  };

  // Проверка аутентификации
  const isAuthenticated = () => {
    const storedToken = localStorage.getItem('token');
    const hasToken = !!token || !!storedToken;
    const hasUser = !!user;
    const authenticated = hasToken && hasUser;

    // Если токен есть, но пользователя нет, пытаемся загрузить
    if (hasToken && !hasUser && !loading) {
      const tokenToUse = token || storedToken;
      if (tokenToUse && token !== tokenToUse) {
        setToken(tokenToUse);
      }
    }
    
    return authenticated;
  };

  // Проверка роли пользователя
  const hasRole = (role) => {
    return user?.role === role;
  };

  // Получить статус пользователя (sport/partner)
  const getUserStatus = () => {
    return user?.userStatus || 'sport';
  };

  // Проверка: является ли пользователь "Спорт"
  const isSport = () => false;

  // Проверка: является ли пользователь "Партнёр" или выше
  const isPartner = () => {
    return getUserStatus() === 'partner';
  };

  // Создание заявки на повышение статуса
  const requestPartnerStatus = async (comment = '') => {
    try {
      const response = await api.post('/users/upgrade-request', { comment });
      return { success: true, message: 'Заявка отправлена', data: response.data };
    } catch (error) {
      const message = error.response?.data?.message || 'Ошибка при отправке заявки';
      return { success: false, message };
    }
  };

  // Получение статуса заявки
  const getUpgradeRequestStatus = async () => {
    try {
      const response = await api.get('/users/upgrade-request/status');
      return { success: true, data: response.data.data };
    } catch (error) {
      const message = error.response?.data?.message || 'Ошибка при получении статуса заявки';
      return { success: false, message };
    }
  };

  const value = {
    user,
    token,
    loading,
    login,
    verify2FA,
    register,
    logout,
    updateProfile,
    verifyEmail,
    resendVerificationCode,
    forgotPassword,
    verifyPasswordResetCode,
    resetPassword,
    resendPasswordResetCode,
    changePassword,
    isAuthenticated,
    hasRole,
    getUserStatus,
    isSport,
    isPartner,
    requestPartnerStatus,
    getUpgradeRequestStatus
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext; 